/**
 * BaseLevel — shared boilerplate for all level scenes.
 *
 * Extracts the duplicated ~140 lines of setup that every level repeats:
 *   - Physics init
 *   - Scene background + fog
 *   - Glow layer
 *   - Player creation (Minna + Basil)
 *   - Ability creation and registration (6 base abilities)
 *   - Camera, Audio, Input, Cutscene system creation
 *   - Game loop (input routing, ability keybinds via AbilityInputMapper)
 *   - Dispose cleanup
 *
 * Each level calls `setupBaseLevel(scene, config)` and gets back a
 * `BaseLevelContext` with all shared systems + helpers. The level then
 * only needs to add its unique content: platforms, puzzles, NPCs, etc.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { isLowFiMode } from "../core/lowFiMode";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";

import { EnergySystem } from "../systems/EnergySystem";
import { HarnessWoundSystem } from "../systems/HarnessWoundSystem";
import { SaveSystem, type CheckpointData, CHECKPOINT_VERSION } from "../systems/SaveSystem";
import { ENERGY, HARNESS, PLAYER, FALL_DAMAGE, POST_PROCESSING } from "../config/GameConfig";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import { computeFallDamage } from "../systems/flow/FallDamage";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import type { PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import type { PuzzleQAHints } from "../levels/qa/PuzzleQAHints";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { clampDeltaTime } from "../systems/time/clampDeltaTime";
import { AudioManager } from "../systems/AudioManager";
import { CutsceneSystem } from "../systems/CutsceneSystem";
import { AbilityInputMapper, AbilityBinding } from "../systems/AbilityInputMapper";
import { AbilityVFX } from "../systems/AbilityVFX";
import { ResonanceSystem } from "../systems/ResonanceSystem";
import { getNavigationSystem } from "../systems/NavigationSystem";
import { decideSoloFollow } from "../systems/navigation/SoloFollow";
import { isOnHoldStation } from "../systems/navigation/HoldStations";
import { StuckAccumulator } from "../systems/navigation/StuckAccumulator";
import { resolveRespawn } from "../systems/flow/RespawnResolver";
import { AccessibilitySystem } from "../systems/AccessibilitySystem";

import { Minna } from "../entities/players/Minna";
import { Basil } from "../entities/players/Basil";
import { AmbientParticleManager } from "../systems/AmbientParticleManager";

import { AuraSight } from "../abilities/minna/AuraSight";
import { ShadowSlip } from "../abilities/minna/ShadowSlip";
import { CalmingResonance } from "../abilities/minna/CalmingResonance";
import { TurnTableTouch, TurnTableTouchConfig } from "../abilities/basil/TurnTableTouch";
import { PneumaticAnchor } from "../abilities/basil/PneumaticAnchor";
import { OverclockedLeap } from "../abilities/basil/OverclockedLeap";
import { qaRulesActive } from "../qa/qaRules";

// ─── Configuration ──────────────────────────────────────────────────

export interface LevelConfig {
  /** Unique key for this level (e.g. "LiminalAtrium") */
  levelKey: string;
  /** Scene clear color */
  clearColor: Color4;
  /** Fog color (omit to skip fog setup) */
  fogColor?: Color3;
  /** Fog density (defaults to 0.018 if fogColor is provided) */
  fogDensity?: number;
  /** Minna spawn position */
  minnaSpawn: Vector3;
  /** Basil spawn position */
  basilSpawn: Vector3;
  /**
   * When true, create EnergySystem instances for both players and wire
   * Kinetic Battery / Ethereal Focus / Co-Regulation into the game loop.
   * Levels 0-1 can omit this (defaults to false) so the energy system
   * doesn't interfere with their existing balance.
   */
  energyEnabled?: boolean;
  /**
   * Custom ability bindings for this level. If omitted, the default set
   * (Q=auraSight, E=shadowSlip, F=calmingResonance, 3=pneumaticAnchor,
   * 4=overclockedLeap) is used. Levels that need to intercept certain keys
   * (e.g. RedwoodScrapyard overrides Q and E) can provide a subset here
   * and handle the rest via onAbilityKey.
   */
  abilityBindings?: AbilityBinding[];
  /**
   * Optional Turn-Table Touch tuning override for this level. Used by the
   * tutorial (Liminal Atrium) to widen the hit window and slow the marker
   * so first-contact with the rhythm mechanic is forgiving. Leave unset
   * to get the default narrow window used by Levels 1-3.
   */
  turnTableTouchConfig?: TurnTableTouchConfig;
}

// ─── Context returned to levels ─────────────────────────────────────

export interface BaseLevelContext {
  scene: Scene;
  physics: PhysicsSystem;
  minna: Minna;
  basil: Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  /** Minna's individual abilities (for level-specific wiring) */
  auraSight: AuraSight;
  shadowSlip: ShadowSlip;
  calmingResonance: CalmingResonance;
  /** Basil's individual abilities (for level-specific wiring) */
  turnTableTouch: TurnTableTouch;
  pneumaticAnchor: PneumaticAnchor;
  overclockedLeap: OverclockedLeap;
  camera: CameraSystem;
  inputManager: InputManager;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
  glowLayer: GlowLayer;
  getGlowLayer(): GlowLayer;
  setGlowLayer(glowLayer: GlowLayer): void;
  getHighlightLayer(): HighlightLayer;
  shadowGenerator: ShadowGenerator;
  /** Energy systems (null when energyEnabled is false). */
  basilEnergy: EnergySystem | null;
  minnaEnergy: EnergySystem | null;
  /**
   * Basil's harness-wound system. Live on every level — the system itself
   * is cheap, and the ability multipliers fall back to 1.0× when
   * integrity is Intact (the default at level start) so tutorials are
   * unaffected. Level 0 is spec'd to never damage the harness.
   */
  harnessWounds: HarnessWoundSystem;
  /** The centralized ability input mapper — levels can use justPressed/justReleased
   *  for level-specific keys (e.g. "1", "2", ","). */
  abilityMapper: AbilityInputMapper;
  /**
   * Register a beforeRender callback that handles input routing,
   * ability keybinds, and camera updates. The level only provides
   * its puzzle-specific update logic.
   *
   * @param puzzleUpdate Called each frame with (dt, sceneTime).
   *   `dt` is in seconds. Return early or skip logic based on level state.
   * @param options.isLevelComplete Checked each frame; skips the loop when true.
   * @param options.onAbilityKey Optional override for level-specific ability keybinds.
   *   Called with (key, justPressed, justReleased). Return true to suppress default handling.
   */
  registerGameLoop(
    puzzleUpdate: (dt: number, sceneTime: number) => void,
    options?: {
      isLevelComplete?: () => boolean;
      onAbilityKey?: (key: string, pressed: boolean, released: boolean) => boolean;
    },
  ): void;
  /** Collect event unsubscribe callbacks for cleanup */
  addUnsub(unsub: () => void): void;
  /** Add extra dispose logic (NPC cleanup, etc.) */
  addDisposeCallback(fn: () => void): void;
  /** Register a guarded before-render callback that routes failures through LEVEL_CRASHED. */
  registerSafeBeforeRender(name: string, callback: () => void): () => void;
  /** Dispose everything (called automatically on scene dispose) */
  dispose(): void;
}

// ─── Checkpoint capture ─────────────────────────────────────────────

/** Minimal read-only surface the checkpoint capture needs from a player. */
interface CheckpointCatRef {
  mesh: { position: { x: number; y: number; z: number } };
}

/** Dependencies for {@link installCheckpointCapture}. */
export interface CheckpointCaptureDeps {
  levelKey: string;
  minna: CheckpointCatRef;
  basil: CheckpointCatRef;
  harnessWounds: { current: number };
  basilEnergy: { current: number } | null;
  minnaEnergy: { current: number } | null;
  /**
   * Optional re-entrancy guard: when the referenced object's `value` is true,
   * checkpoint capture is suppressed. Set to true at the START of
   * restoreFromCheckpoint and false in a finally block so synthetic
   * PUZZLE_SOLVED events emitted by forceSolvePuzzle during restore cannot
   * overwrite the persisted checkpoint with spawn-position cats.
   */
  restoringRef?: { value: boolean };
}

/**
 * Subscribe to PUZZLE_SOLVED and auto-capture a v2 CheckpointData on each
 * DISTINCT solved puzzle. Capture is strictly read-only: it copies cat
 * positions, harness integrity, and energy scalars without writing any
 * transform/velocity (so the QA CatNavigator is never perturbed).
 *
 * Returns an unsubscribe function (wire it through `addUnsub`).
 */
export function installCheckpointCapture(deps: CheckpointCaptureDeps): () => void {
  const solvedOrder: string[] = [];
  const seen = new Set<string>();

  const snapshot = (): void => {
    const checkpoint: CheckpointData = {
      version: CHECKPOINT_VERSION,
      levelId: deps.levelKey,
      puzzleIndex: solvedOrder.length - 1,
      solvedPuzzleIds: [...solvedOrder],
      cats: {
        minna: {
          x: deps.minna.mesh.position.x,
          y: deps.minna.mesh.position.y,
          z: deps.minna.mesh.position.z,
        },
        basil: {
          x: deps.basil.mesh.position.x,
          y: deps.basil.mesh.position.y,
          z: deps.basil.mesh.position.z,
        },
      },
      harnessIntegrity: deps.harnessWounds.current,
      energy: {
        minna: deps.minnaEnergy?.current ?? 0,
        basil: deps.basilEnergy?.current ?? 0,
      },
      // SCALARS ONLY — Task 3+ extends this with per-puzzle restore flags.
      puzzleState: {},
    };

    SaveSystem.getInstance().saveCheckpoint(checkpoint);
    gameEvents.emit(GameEvents.CHECKPOINT_SAVED, {
      levelId: deps.levelKey,
      puzzleIndex: checkpoint.puzzleIndex,
    });
  };

  const unsubSolved = gameEvents.on(GameEvents.PUZZLE_SOLVED, (data) => {
    // C1 guard: suppress capture while restoreFromCheckpoint is running so
    // synthetic PUZZLE_SOLVED events from forceSolvePuzzle cannot overwrite
    // the persisted checkpoint with pre-reposition (spawn) cat coordinates.
    if (deps.restoringRef?.value) return;
    const puzzleId = data?.puzzleId;
    if (!puzzleId || seen.has(puzzleId)) return;
    seen.add(puzzleId);
    solvedOrder.push(puzzleId);
    snapshot();
  });

  // Mid-puzzle checkpoint markers (e.g. the Conservatory escort's glowing
  // waypoint spheres). Before this, those markers only updated a private
  // field whose getRespawnPosition() had no caller — so a death during the
  // escort or apex teleported the player all the way back to the last
  // puzzle-solve position (~x=22) despite having walked past a checkpoint
  // at x=110. Snapshotting here routes the existing respawn resolver
  // (checkpoint → partner → spawn) to the touched marker.
  const unsubTouched = gameEvents.on(GameEvents.CHECKPOINT_TOUCHED, () => {
    if (deps.restoringRef?.value) return;
    snapshot();
  });

  return () => {
    unsubSolved();
    unsubTouched();
  };
}

// ─── Setup ──────────────────────────────────────────────────────────

/**
 * Set up the shared boilerplate for a level scene.
 *
 * This initializes physics, creates players, registers all 6 base
 * abilities, sets up camera/audio/input/cutscene systems, and provides
 * a `registerGameLoop` helper that wires input routing + ability keybinds
 * via AbilityInputMapper.
 */
export async function setupBaseLevel(
  scene: Scene,
  config: LevelConfig,
): Promise<BaseLevelContext> {
  // --- Physics ---
  const physics = new PhysicsSystem(scene);
  await physics.init();

  // --- Scene background ---
  scene.clearColor = config.clearColor;

  // --- Fog ---
  if (config.fogColor) {
    scene.fogMode = Scene.FOGMODE_EXP2;
    scene.fogDensity = config.fogDensity ?? 0.018;
    scene.fogColor = config.fogColor;
  }

  // --- IBL environment for PBR metals/glass/reflections ---
  // Without this, every PBRMaterial metal renders near-black since it
  // has nothing to reflect. Served from our own origin: the previous
  // https://assets.babylonjs.com URL made every level's material quality
  // depend on a third-party CDN at runtime, so players (and headless QA)
  // without access to that host got black metals. The vendored file is a
  // prefiltered warm-horizon gradient authored for the game's palette.
  if (!scene.environmentTexture) {
    const envTexture = CubeTexture.CreateFromPrefilteredData(
      "/assets/env/environmentSpecular.env",
      scene,
    );
    scene.environmentTexture = envTexture;
    scene.environmentIntensity = 0.6;
  }

  // --- Shared effect layers ---
  let glowLayer = new GlowLayer("glow", scene, { blurKernelSize: 32 });
  glowLayer.intensity = 0.8;
  const highlightLayer = new HighlightLayer("sharedHighlight", scene, {
    blurHorizontalSize: 0.5,
    blurVerticalSize: 0.5,
  });
  highlightLayer.isEnabled = false;

  // --- Post-processing pipeline ---
  let pipeline: DefaultRenderingPipeline | null = null;
  if (!isLowFiMode()) {
    try {
      const postConfig = POST_PROCESSING[config.levelKey as keyof typeof POST_PROCESSING] ?? POST_PROCESSING.DEFAULT;
      pipeline = new DefaultRenderingPipeline("levelPipeline", true, scene, scene.cameras);
      pipeline.bloomEnabled = postConfig.bloomEnabled;
      pipeline.bloomWeight = postConfig.bloomWeight;
      pipeline.bloomThreshold = postConfig.bloomThreshold;
      pipeline.bloomKernel = postConfig.bloomKernel;
      pipeline.chromaticAberrationEnabled = postConfig.chromaticAberrationEnabled;
      pipeline.chromaticAberration.aberrationAmount = postConfig.chromaticAmount;

      if (AccessibilitySystem.getInstance().state.reducedMotion) {
        pipeline.bloomEnabled = false;
        pipeline.chromaticAberrationEnabled = false;
      }

      gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
        if (pipeline) {
          const a11y = AccessibilitySystem.getInstance().state;
          if (a11y.reducedMotion) {
            pipeline.bloomEnabled = false;
            pipeline.chromaticAberrationEnabled = false;
          } else {
            pipeline.bloomEnabled = postConfig.bloomEnabled;
            pipeline.chromaticAberrationEnabled = postConfig.chromaticAberrationEnabled;
          }
        }
      });
    } catch (err) {
      console.warn("[Cats of Chronos] Pipeline init warning:", err);
    }
  }


  // --- Low-fi mode: suppress dynamic point/spot lights scene-wide ---
  // Ranged point lights attached to moving entities (fox eyes, Elder
  // beacons, crystal accents) constantly change which meshes they
  // illuminate; each membership change regenerates material defines and
  // forces a shader re-link. On software GL a link costs ~1 second, so
  // this churn alone pins the game at ~1 fps. Lights are cosmetic —
  // nothing in gameplay reads them — so low-fi disables every non-rig
  // light at creation time. The hemispheric + directional rig from the
  // level's VisualProfile is untouched (stable define set).
  if (isLowFiMode()) {
    glowLayer.isEnabled = false;
    const suppressDynamicLight = (light: unknown): void => {
      if (light instanceof PointLight || light instanceof SpotLight) {
        light.setEnabled(false);
      }
    };
    for (const light of scene.lights) suppressDynamicLight(light);
    // Observer lifetime is scene-bound: onNewLightAddedObservable dies
    // with the scene, so no explicit removal is needed.
    scene.onNewLightAddedObservable.add((light) => {
      suppressDynamicLight(light);
    });
  }

  // --- Players ---
  const minna = new Minna(scene, config.minnaSpawn);
  const basil = new Basil(scene, config.basilSpawn);

  // Freeze both players as KINEMATIC (motion type 1) during scene
  // setup. Each scene creates ground/platforms AFTER awaiting GLB
  // loads — in L3 ClockworkCanopy, `createGroundAndPlatforms`
  // currently runs ~3-5s after the BaseLevel setup returns. If the
  // players were DYNAMIC from frame 0 they would free-fall through
  // the (as yet empty) void for 3-5 seconds before platforms caught
  // them, firing kill-plane respawns + HUD toasts during what should
  // be a clean level-load state. 2026-04-18 visual review: "L3
  // gameplay-start shows void + 'respawned' toasts" P0.
  //
  // `thawPlayers()` on the base handle flips both back to DYNAMIC.
  // Scenes call it explicitly after `createGroundAndPlatforms` so
  // physics simulation starts against real colliders. Falls back to
  // auto-thaw on the first `__levelReady = true` flip if a scene
  // forgets.
  try {
    minna.aggregate?.body.setMotionType(1);
    basil.aggregate?.body.setMotionType(1);
  } catch {
    // Tests without Havok plugin — no bodies to freeze, so every
    // motion-type call is a no-op anyway.
  }

  // --- Abilities ---
  const minnaAbilities = new AbilityHolder("minna");
  const basilAbilities = new AbilityHolder("basil");

  const auraSight = new AuraSight(scene, minna.mesh, glowLayer, highlightLayer);
  const shadowSlip = new ShadowSlip(scene, minna.mesh);
  const calmingResonance = new CalmingResonance(scene, minna.mesh);

  minnaAbilities.addAbility(auraSight);
  minnaAbilities.addAbility(shadowSlip);
  minnaAbilities.addAbility(calmingResonance);

  const turnTableTouch = new TurnTableTouch(scene, basil.mesh, config.turnTableTouchConfig);
  // Grounded gate prevents activating the dial mid-air in real play. The QA
  // navigator drives activation programmatically at moments Basil may not read
  // grounded, so bypass the gate when the QA surface is present (consistent
  // with the solo-assist qaActive gating) — QA tests puzzle logic, not the gate.
  turnTableTouch.setGroundedProvider(
    () =>
      qaRulesActive() ||
      basil.grounded,
  );
  const pneumaticAnchor = new PneumaticAnchor(scene, basil.mesh, basil.aggregate.body);
  const overclockedLeap = new OverclockedLeap(scene, basil.mesh, basil.aggregate.body);

  basilAbilities.addAbility(turnTableTouch);
  basilAbilities.addAbility(pneumaticAnchor);
  basilAbilities.addAbility(overclockedLeap);

  // Inject the canonical holder + PneumaticAnchor into Basil so
  // `applyInput`'s "ability" button routes through the SAME ability
  // instances that AbilityInputMapper activates and `basilAbilities.update`
  // ticks. Without this, Basil used to own a parallel set of instances
  // whose `chargeTimer` never advanced (and whose `isActive` toggled
  // independently from the one `ability:activated` emits on). See
  // `docs/qa/analysis/startatpuzzle-audit-2026-04-18.md` §2.4.
  basil.setAbilityContext(basilAbilities, pneumaticAnchor);

  // --- Harness Wound System (always live) ---
  // Composes WITH EnergySystem per `docs/specs/SPEC-wounds-and-medicine.md`.
  // Default Intact state → all tuning multipliers = 1.0×, so Level 0 and
  // any level that doesn't actually damage Basil is unaffected.
  const harnessWounds = new HarnessWoundSystem({ character: "basil" });

  // Wire per-state tuning providers into Basil's abilities. Each
  // provider reads `harnessWounds.tuning` on every call so live damage
  // mid-run immediately affects the next activation.
  turnTableTouch.setTuningProvider(() => harnessWounds.tuning.ttt);
  pneumaticAnchor.setTuningProvider(() => harnessWounds.tuning.anchor);
  overclockedLeap.setTuningProvider(() => harnessWounds.tuning.leap);

  // --- Camera ---
  // Dynamic-zoom follow cam is span-aware: when the cats are close the
  // shot tightens to CAMERA.Z_DISTANCE; as they separate it pulls out
  // toward CAMERA.MAX_Z_DISTANCE. No separate establishing-shot mode —
  // the tight-to-wide behavior handles both first-read and split play.
  const camera = new CameraSystem(scene);
  camera.setTargets([minna.mesh, basil.mesh]);
  // Register physics bodies for horizontal lookahead velocity sampling
  // (read-only — CameraSystem never mutates these bodies).
  camera.setPhysicsBodies([
    minna.aggregate?.body ?? null,
    basil.aggregate?.body ?? null,
  ]);
  // Snap BEFORE `scene.activeCamera = camera.camera` so the first
  // render (and screenshots taken immediately after level load) show
  // the cats framed, not the default (0, 1.2, 9) pose lerping in.
  // Without this, the first ~10-20 frames of a level show an empty
  // void in the sky box direction. P0 finding in 2026-04-18 visual
  // review (L3 gameplay-start captured the void + "respawned" toasts
  // well before the camera caught up to the cats).
  camera.snapToTargets();
  scene.activeCamera = camera.camera;

  // --- Audio ---
  const audioManager = new AudioManager(scene, config.levelKey);

  // --- Cutscene system ---
  const cutsceneSystem = new CutsceneSystem(scene, camera);
  // Stash on the scene so late-attached systems (PauseMenu) can query
  // `isActive` without depending on having witnessed CUTSCENE_STARTED.
  (scene as unknown as { __cutsceneSystem?: CutsceneSystem }).__cutsceneSystem = cutsceneSystem;
  if (import.meta.env.DEV) {
    (window as unknown as { __cutscene?: CutsceneSystem }).__cutscene = cutsceneSystem;
  }

  // --- Input ---
  const inputManager = new InputManager();

  // Dev-only QA hook: let the playthrough recorder force solo mode so
  // existing solo-style traces (Tab + WASD + ability digits) still
  // drive gameplay after the default flipped to local co-op. Co-op
  // traces would require scripting both halves of the keyboard.
  if (import.meta.env.DEV) {
    (window as unknown as { __forceSolo?: (v: boolean) => void }).__forceSolo = (v: boolean) => {
      inputManager.soloMode = v;
    };

    // ─── Dev-only QA hook: __startAtPuzzle ─────────────────────────
    // Loads a level AND fast-forwards the puzzle chain to the start of
    // a specific puzzle. This makes each puzzle independently testable
    // without running the preceding ones. Cuts 90s recording cycles to
    // ~20s for non-tutorial levels.
    //
    // Usage:   window.__startAtPuzzle("ClockworkCanopy", "lightPath")
    //
    // Returns a promise that resolves once the level is loaded, earlier
    // puzzles are marked solved, any post-previous-solve cutscene is
    // dismissed, and the cats have been teleported into the target
    // puzzle's staging area.
    //
    // Full fast-forward (getState clamp + levelState seeding + event
    // emission) only lands for levels whose scene has called
    // `scene.metadata.level.registerPuzzles(...)`. Scenes that haven't
    // yet adopted the registration get event-only fast-forward (emits
    // `PUZZLE_SOLVED` per skipped puzzle) which is enough for listeners
    // that gate on events but may leave stateful puzzle internals
    // (e.g. a half-spun dial) in their pre-solved state.
    interface StartAtPuzzleOptions {
      /** Skip the teleport-to-origin step. Default: false. */
      skipTeleport?: boolean;
      /**
       * Optional sub-puzzle state seed. Passed to the target puzzle's
       * `seed()` method after teleport + cutscene-skip so the trace
       * starts with pre-advanced internal state (e.g. lightPath:
       * `{ lensesPlaced: [0, 1] }` skips the first two lens carries).
       * Puzzles that don't implement `seed` ignore this.
       */
      seedState?: Record<string, unknown>;
    }
    const PUZZLE_ORIGINS: Record<string, Record<string, { x: number; y: number }>> = {
      // Scene section X ranges from each scene file's header comments.
      // Keep the Y low enough that cats drop onto their section's
      // platform rather than spawning in midair.
      LiminalAtrium: {
        tutorialGate: { x: 4, y: 3 },
      },
      // Puzzle-origin table. Drops the cats onto the walkable
      // ground/platform for each puzzle's section. Y values put capsule
      // center just above the target platform-top + 0.7 (capsule half)
      // so the cats fall a single tick and land with zero residual vy.
      // Picked to avoid mid-air dead-space between staircase platforms
      // where the original "y:5 for every puzzle" deposit could wedge
      // a capsule on a platform lip.
      RedwoodScrapyard: {
        // Ground is a single mesh centered at x=0 with width 154 → it
        // covers ONLY x=-77..+77. Puzzles east of x=77 have to drop
        // cats onto their section's local platform (p3_*, p4_*, p5_*)
        // or the cats fall into the void between sections. Y values
        // below are calibrated to place capsule-center just above the
        // intended platform TOP (+~1u margin so the 0.7u capsule half
        // settles cleanly).
        seizedCrane:   { x: 8,   y: 3 },    // onto p1_1 (top y=2.2)
        crowSquirrel:  { x: 40,  y: 1 },    // ground (ground extends to +77)
        kittenCorral:  { x: 75,  y: 3.2 },  // onto p3_1 (top y=2.2). x=75 was
                                            // x=80 but that's in the 77→110
                                            // gap — no platform there.
        hoodieJigsaw:  { x: 118, y: 5 },    // onto p4_2 (top y=4.2). x=115/y=1
                                            // dropped cats below every local
                                            // apex platform into void.
        finalBlockage: { x: 138, y: 3.5 },  // onto p5_1 (top y=2.7). x=138/y=1
                                            // sat below p5_1 and the ground
                                            // doesn't reach past x=77.
      },
      OvergrownConservatory: {
        // Conservatory ground width 160 → covers x=-80..+80. Puzzles
        // east of x=80 need per-puzzle platform targets. The escort
        // ladders + sundew boiler provide local colliders.
        masterValve:       { x: 18,  y: 1 },    // ground (x=18 inside 80)
        nectarEscort:      { x: 108, y: 1 },    // ground east of the lowered escort rungs;
                                                 // x=105/y=3 now lands on ladder A_1 and
                                                 // wedges the route before opossum_0.
        sundewApex:        { x: 133, y: 3.2 },  // runtime puzzle id for BoilerBoundSundew;
                                                 // onto sundew root platform (x=133,
                                                 // top ~2.4). x=128 / y=1 was also void.
        boilerBoundSundew: { x: 133, y: 3.2 },  // legacy alias retained for older traces/docs.
      },
      ClockworkCanopy: {
        // Canopy ground mesh width 140 → covers x=-70..+70. Puzzles
        // east of x=70 need their origin on a local `plat_s*` collider.
        gearAlignment:  { x: 10, y: 3 },       // onto plat_s1_1 (top 2.2)
        lightPath:      { x: 30, y: 3 },       // onto plat_s2_1 (#13 widened to
                                                // x=[27,35], top 2.2). Was y=1 /
                                                // ground — only barely inside ground
                                                // (-70..+70) and inconsistent with
                                                // the plat-based peers.
        timedResonance: { x: 65, y: 1 },       // ground (x=65 inside 70)
        shadowRelay:    { x: 82, y: 3 },       // onto plat_s4_1 (top 2.2). x=82 is
                                                // outside ground; y=1 dropped cats
                                                // into void.
        theOrrery:      { x: 115, y: 4 },      // onto plat_s5_1 (top 3.2). x=115
                                                // outside ground; y=1 was void.
      },
    };

    (window as unknown as {
      __startAtPuzzle?: (
        levelKey: string,
        puzzleId: string,
        options?: StartAtPuzzleOptions,
      ) => Promise<void>;
    }).__startAtPuzzle = async (
      levelKey: string,
      puzzleId: string,
      options: StartAtPuzzleOptions = {},
    ): Promise<void> => {
      const w = window as unknown as {
        __loadLevel?: (key: string) => void | Promise<void>;
        __levelReady?: boolean;
        __cutscene?: CutsceneSystem;
        __scene?: Scene;
      };
      if (typeof w.__loadLevel !== "function") {
        throw new Error("__startAtPuzzle: __loadLevel is not available");
      }

      // (1) Trigger the normal level load unless we're already on the
      // correct level. The QASurface fallback boots the level first so
      // that this hook becomes installed — re-loading then would waste
      // ~5-10s per startAtPuzzle call. Only reload if the scene is on a
      // different level.
      const currentLevelKey = (w.__scene?.metadata as { level?: { levelKey?: string } } | undefined)
        ?.level?.levelKey;
      if (currentLevelKey !== levelKey) {
        w.__levelReady = false;
        await w.__loadLevel(levelKey);
      }

      // (2) Wait for `__levelReady === true`. We poll rather than hook
      // an event because `__levelReady` is what the Playwright harness
      // itself waits on, so using it here keeps behavior consistent.
      const READY_TIMEOUT_MS = 15000;
      const readyStart = performance.now();
      while (!w.__levelReady) {
        if (performance.now() - readyStart > READY_TIMEOUT_MS) {
          throw new Error(
            `__startAtPuzzle: timed out waiting for ${levelKey} __levelReady`,
          );
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
      }

      // Resolve the handle from the NEW scene (not the closure above —
      // main.ts recreates the scene across level loads, so any closure
      // reference to `scene` from this setup call is stale after the
      // first `__startAtPuzzle`).
      const activeScene = w.__scene;
      const handle = (activeScene?.metadata as { level?: LevelHandle } | undefined)?.level;
      if (!handle) {
        throw new Error(`__startAtPuzzle: no level handle on scene for ${levelKey}`);
      }

      const loadedIds =
        ((activeScene?.metadata as { loadedPuzzleIds?: string[] } | undefined)?.loadedPuzzleIds) ??
        [];
      if (!loadedIds.includes(puzzleId)) {
        throw new Error(
          `__startAtPuzzle: unknown puzzle "${puzzleId}" in ${levelKey}. ` +
            `Known ids: ${loadedIds.join(", ") || "(none)"}`,
        );
      }

      // (3) Walk the puzzles in order, marking each one BEFORE the target
      // as solved. `forceSolvePuzzle` both (a) seeds levelState and (b)
      // emits PUZZLE_SOLVED so downstream listeners (apex gating, HUD
      // prompts, unlock events) see consistent state. Fallback for
      // legacy scenes that haven't called registerPuzzles yet: still
      // emit PUZZLE_SOLVED so event-driven gates advance.
      const targetIndex = loadedIds.indexOf(puzzleId);
      for (let i = 0; i < targetIndex; i++) {
        const id = loadedIds[i];
        const marked = handle.forceSolvePuzzle(id);
        if (!marked) {
          // Legacy path — emit the event so any gate that listens for
          // `PUZZLE_SOLVED` still advances. This is intentionally
          // noisier than `forceSolvePuzzle` because without the getState
          // clamp the puzzle's own `getState()` will still return
          // `solved: false` on the next frame.
          gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: id });
        }
      }

      // (5) Cancel any pending intro cutscene and abort any playing
      // cutscene, then poll briefly to let any in-flight step teardown
      // finish. Without this, a setTimeout-deferred intro would fire
      // ~600ms after rebuild and freeze the game-loop for ~16s (because
      // `registerGameLoop` short-circuits on `cutsceneSystem.playing`),
      // during which `ability:activated` emits land but no ability
      // update / onAbilityKey callbacks run — breaking trace playback.
      // See docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
      //
      // `skipAll()` (not `stop()`) so callback steps (which scenes rely
      // on to mutate state — e.g. enabling Elder meshes) still fire;
      // only the wait/dialogue/pan delays are skipped.
      const cs = (activeScene as unknown as { __cutsceneSystem?: CutsceneSystem } | undefined)
        ?.__cutsceneSystem;
      if (cs) {
        cs.cancelPending();
        if (cs.isActive) cs.skipAll();
      }
      // Poll for cutscene-cleared (generous 2s budget in case skipAll
      // chains into a fade-out or async-completing step).
      const cutsceneClearedBy = Date.now() + 2000;
      while (Date.now() < cutsceneClearedBy) {
        const stillActive = (
          activeScene as unknown as { __cutsceneSystem?: CutsceneSystem } | undefined
        )?.__cutsceneSystem?.isActive;
        if (!stillActive) break;
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
      }

      // (4) Reposition cats to the target puzzle's intended staging
      // area. Physics-safe — `handle.teleport` mirrors the MCP bridge's
      // `teleport_character` pattern (disablePreStep toggle + velocity
      // zeroing) so the Havok body doesn't stomp the new transform.
      if (!options.skipTeleport) {
        const origin = PUZZLE_ORIGINS[levelKey]?.[puzzleId];
        if (origin) {
          // Stagger Minna and Basil by 2.5 units so they don't overlap
          // capsules on spawn — mirrors the default spawn stagger used
          // in every level config but slightly wider to prevent kittenCorral pushes.
          handle.teleport("minna", origin.x - 1.25, origin.y, 0);
          handle.teleport("basil", origin.x + 1.25, origin.y, 0);
          // Snap the camera to the new cat positions so the very next
          // render frames the puzzle area. Without this, the camera
          // lerps from the level's default spawn — typically 20-100
          // units west of east-side puzzles — and the first 1-2 seconds
          // show empty sky while it catches up. Surfaced in 2026-04-18
          // visual review as the L3 "void + respawned toasts" P0.
          const cam = (activeScene as unknown as {
            metadata?: { cameraSystem?: { snapToTargets: () => void } };
          })?.metadata?.cameraSystem;
          cam?.snapToTargets();
        } else {
          console.warn(
            `[__startAtPuzzle] No known origin for ${levelKey}/${puzzleId}; ` +
              `cats left at their level spawn.`,
          );
        }
      }

      // (5a) Sub-puzzle state seed. If the caller passed `options.seedState`
      // and the target puzzle exposes a `seed` method, invoke it so the
      // trace starts with pre-advanced state (e.g. lightPath with the
      // first two lenses already placed). Routes through the handle's
      // `seedPuzzle` so we don't reach into a stale `registeredPuzzles`
      // closure — the __startAtPuzzle hook is installed once but serves
      // every level, so we always re-resolve via `scene.metadata.level`.
      if (options.seedState) {
        const ok = handle.seedPuzzle(puzzleId, options.seedState);
        if (!ok) {
          console.warn(
            `[__startAtPuzzle] ${levelKey}/${puzzleId} has no seed() method; seedState ignored.`,
          );
        }
      }

      // (6) Give the scene one render tick so forced-solve state can
      // propagate through listeners before the caller's trace starts
      // dispatching input.
      await new Promise<void>((resolve) => {
        activeScene?.onAfterRenderObservable.addOnce(() => resolve());
        // Safety net: some idle scenes don't tick onAfterRender until
        // input arrives. Fall back to a microtask after 200ms.
        setTimeout(resolve, 200);
      });
    };
  }

  // --- Ability Input Mapper (centralized keybinds) ---
  const abilityMapper = new AbilityInputMapper(config.abilityBindings);

  // --- Ability VFX ---
  // One-shot particle bursts hooked to ability events. Every level gets
  // these for free — see `systems/AbilityVFX.ts` for the event map.
  const abilityVfx = new AbilityVFX(scene, minna.mesh, basil.mesh);

  // --- Resonance System ---
  const resonanceSystem = new ResonanceSystem(scene, minna.mesh, basil.mesh, gameEvents);
  // Glow-exclude the co-regulation stream: with the GlowLayer halo it
  // read as a laser beam between the cats at eye height in every level
  // (2026-07-10 visual review, Q-023). Its own emissive alpha carries it.
  const resonanceStreamMesh = resonanceSystem.getStreamMesh() as Mesh | null;
  if (resonanceStreamMesh) {
    glowLayer.addExcludedMesh(resonanceStreamMesh);
  }

  // --- Atmospheric Particle System ---
  const ambientParticles = new AmbientParticleManager(scene, config.levelKey);

  // --- Cleanup tracking ---
  const unsubs: (() => void)[] = [];
  const disposeCallbacks: (() => void)[] = [
    () => resonanceSystem.dispose(),
    () => ambientParticles.dispose(),
  ];


  // --- Harness Wound telemetry state (I1: net-drop model) ---
  // Track each cat's Y while grounded (lastGroundedY). While airborne,
  // freeze the value. On landing, netDrop = max(0, lastGroundedY - landingY).
  // This is zero for any jump/OvLeap that returns to the same height, and
  // positive only for genuine falls off ledges — so fast-fall input and the
  // OverclockedLeap boost no longer cause harness damage on routine jumps.
  // NAVIGATOR SAFETY: reads the public `grounded` getter only — never writes isGrounded.
  let basilLastGroundedY = config.basilSpawn.y;
  let minnaLastGroundedY = config.minnaSpawn.y;

  // Hoisted so the PUZZLE_FAILED handler (below) can close over it before
  // levelHandle is constructed. The event only fires at runtime (after
  // construction), so this is always populated before the closure is invoked.
  let levelHandle: LevelHandle;

  // Fall-damage + TTT-miss subscriptions (always live; harness starts
  // at Intact and abilities unaffected until damage lands).
  unsubs.push(
    gameEvents.on(GameEvents.PLAYER_LANDED, (who) => {
      // Compute net drop: how far below the last grounded Y the cat landed.
      // Reset the cached takeoff Y after reading so the next arc starts clean.
      let dropDistance = 0;
      if (who === "basil") {
        dropDistance = Math.max(0, basilLastGroundedY - basil.mesh.position.y);
        basilLastGroundedY = basil.mesh.position.y;
      } else if (who === "minna") {
        dropDistance = Math.max(0, minnaLastGroundedY - minna.mesh.position.y);
        minnaLastGroundedY = minna.mesh.position.y;
      } else {
        return;
      }

      // NAVIGATOR SAFETY: skip all fall-damage effects when the QA
      // navigator is active so it cannot accrue harness depletion,
      // trigger PUZZLE_FAILED, or cause HUD spam during qa:fullplay.
      const qaActive =
        qaRulesActive();
      if (qaActive) return;

      const fall = computeFallDamage({
        dropDistance,
        lightThreshold: FALL_DAMAGE.LIGHT_THRESHOLD,
        heavyThreshold: FALL_DAMAGE.HEAVY_THRESHOLD,
        lightDamage: FALL_DAMAGE.LIGHT_DAMAGE,
        heavyDamage: FALL_DAMAGE.HEAVY_DAMAGE,
      });
      if (fall.severity === "none") return;

      if (who === "basil") {
        // Basil's harness absorbs the hit (feeds Task 7's integrity-0 fail).
        harnessWounds.applyDamage(fall.damage, "fall");
      } else {
        // Minna has no harness: FEEDBACK ONLY. land.wav already plays for
        // both cats via AudioManager's PLAYER_LANDED listener; add a camera
        // shake so the hard landing reads. Reduced-motion is force-zeroed
        // inside triggerShake.
        camera.triggerShake(
          FALL_DAMAGE.MINNA_SHAKE_INTENSITY,
          FALL_DAMAGE.MINNA_SHAKE_MS,
        );
      }
    }),
  );
  // Rhythm coaching: presses during the post-miss fumble lockout also miss,
  // so a player mashing 2 never lands a hit. Tell them why, once in a while.
  let tttMissStreak = 0;
  let tttLastCoachAt = -Infinity;
  unsubs.push(
    gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => { tttMissStreak = 0; }),
  );
  unsubs.push(
    gameEvents.on(GameEvents.TURN_TABLE_TOUCH_MISS, () => {
      tttMissStreak++;
      const now = performance.now();
      if (tttMissStreak >= 6 && now - tttLastCoachAt > 10_000) {
        tttLastCoachAt = now;
        tttMissStreak = 0;
        gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Easy, Basil — wait for the marker to reach the glowing notch, then tap 2 once.",
          color: "#c49b3c",
        });
      }
    }),
  );
  unsubs.push(
    gameEvents.on(GameEvents.TURN_TABLE_TOUCH_MISS, () => {
      // Spec §4: missed rhythm damages ONLY while already Venting
      // (keeps tutorial/early-level play forgiving).
      if (harnessWounds.getState() === "venting") {
        harnessWounds.applyDamage(
          HARNESS.DAMAGE.FAILED_RHYTHM_VENTING,
          "turnTableMiss",
        );
      }
    }),
  );
  // Elder encounters restore the harness to Intact (per spec §4
  // "Tertiary: Elder Encounters"). Mirrors the "Making Biscuits"
  // heal-on-purr cadence without stepping on other agents' scope.
  unsubs.push(
    gameEvents.on(GameEvents.ELDER_ENCOUNTER, () => {
      harnessWounds.fullHeal("elder");
    }),
  );

  // --- Phase 5 light-fail: PUZZLE_FAILED -> reload checkpoint ---
  // Harness-zero (HarnessWoundSystem) and escort-lost (NectarEscort) both emit
  // PUZZLE_FAILED. BaseLevel ONLY restores (the HUD owns the single fail toast,
  // MC3). The teleport-restore is gated on !qaActive so the QA CatNavigator
  // never sees an out-of-band position write mid-navigate (mirrors the solo-warp
  // gate). The restore routes every cat teleport through
  // respawnCharacter(reason:'restore') and emits CHECKPOINT_RESTORED — that
  // plumbing is owned by Task 6's levelHandle.restoreFromCheckpoint().
  //
  // Debounce guard: a rapid burst of PUZZLE_FAILED events (e.g. escort-lost
  // followed immediately by harness-zero in the same frame) must not call
  // restoreFromCheckpoint() more than once. `failReloadPending` is set on the
  // first accepted event and cleared by CHECKPOINT_RESTORED so subsequent
  // fail cycles work normally.
  let failReloadPending = false;
  unsubs.push(
    gameEvents.on(GameEvents.PUZZLE_FAILED, () => {
      if (failReloadPending) return;
      const qaActive =
        qaRulesActive();
      if (!qaActive) {
        failReloadPending = true;
        levelHandle.restoreFromCheckpoint();
      }
    }),
  );
  unsubs.push(
    gameEvents.on(GameEvents.CHECKPOINT_RESTORED, () => {
      failReloadPending = false;
    }),
  );

  // --- Energy systems (opt-in per level) ---
  let basilEnergy: EnergySystem | null = null;
  let minnaEnergy: EnergySystem | null = null;
  let coRegulationActive = false;

  if (config.energyEnabled) {
    basilEnergy = new EnergySystem({
      maxEnergy: ENERGY.MAX,
      rechargeRate: ENERGY.BASIL.PASSIVE_RECHARGE,
      depletedThreshold: ENERGY.DEPLETED_THRESHOLD,
      character: 'basil',
    });
    minnaEnergy = new EnergySystem({
      maxEnergy: ENERGY.MAX,
      rechargeRate: 0, // Minna recharges via shadow zones + co-regulation, not passively everywhere
      depletedThreshold: ENERGY.DEPLETED_THRESHOLD,
      character: 'minna',
    });

    // --- Basil recharge on NPC interactions ---
    unsubs.push(
      gameEvents.on(GameEvents.FOOD_DELIVERED, () => {
        basilEnergy!.recharge(ENERGY.BASIL.NPC_INTERACTION_BURST);
      }),
    );
    unsubs.push(
      gameEvents.on(GameEvents.NPC_CALMED, () => {
        basilEnergy!.recharge(ENERGY.BASIL.NPC_INTERACTION_BURST);
      }),
    );

    // --- Basil energy gate: drain on activation, block when depleted ---
    const basilDrainMap: Record<string, number> = {
      overclockedLeap: ENERGY.BASIL.DRAIN.OVERCLOCKED_LEAP,
      resonantDrop: ENERGY.BASIL.DRAIN.RESONANT_DROP,
    };
    basilAbilities.setEnergyGate((abilityId: string) => {
      const baseCost = basilDrainMap[abilityId];
      if (baseCost !== undefined) {
        if (basilEnergy!.isDepleted) return false; // blocked when depleted
        // Wounds/Medicine: scale the drain per harness state so a
        // wounded Basil pays more for each heavy ability use.
        let scale = 1;
        if (abilityId === "overclockedLeap") {
          scale = overclockedLeap.drainScale;
        } else if (abilityId === "resonantDrop") {
          scale = harnessWounds.tuning.shockwave.drainScale;
        }
        return basilEnergy!.drain(baseCost * scale);
      }
      // PneumaticAnchor drain is handled per-frame in the game loop
      return true;
    });

    // --- Minna energy gate: no activation cost, but AuraSight flickers when depleted ---
    // (Minna's drain is continuous per-frame, not per-activation)
    // No gate needed — the per-frame drain + depleted penalties handle it.
  }

  // ─── Auto-checkpoint on puzzle solve (Phase 5) ────────────────────
  // Read-only snapshot of cats/harness/energy + solved-puzzle list.
  // Does NOT move anything, so the QA navigator is unaffected.
  // C1: restoringRef is set true during restoreFromCheckpoint so synthetic
  // PUZZLE_SOLVED events from forceSolvePuzzle cannot overwrite the persisted
  // checkpoint with pre-reposition (spawn) cat coordinates.
  const restoringRef = { value: false };
  unsubs.push(
    installCheckpointCapture({
      levelKey: config.levelKey,
      minna,
      basil,
      harnessWounds,
      basilEnergy,
      minnaEnergy,
      restoringRef,
    }),
  );

  // --- Shadow generator placeholder (must be set by the level's lighting setup) ---
  // Levels create their own lighting + shadow generator and assign it here.
  // We use a mutable container so registerGameLoop closures see the final value.
  let shadowGenerator: ShadowGenerator = null!;
  let runtimeFailed = false;

  // --- Respawn + kill plane ---
  // Any cat that drops below this Y snaps back to its spawn. Keeps the
  // tutorial from soft-locking when a held key walks a character off the
  // edge of the world (real complaint from a playtest run).
  //
  // A per-character grace window blocks back-to-back respawns: if the
  // Havok body hasn't settled by the time the kill-plane check fires
  // again on the next frame, we used to spam respawn + toast + emit
  // loop. Gemini's L2 fluidity review flagged the stacking toasts as a
  // visible bug — the fix is to hold the kill-plane off until the cat
  // has had time to actually reach its spawn y.
  const KILL_Y = -8;
  const RESPAWN_TOAST_COOLDOWN_MS = 1500;
  const RESPAWN_GRACE_MS = 900;
  const lastRespawnToastAt = new Map<string, number>();
  const lastRespawnAt = new Map<string, number>();

  function respawnCharacter(
    which: "minna" | "basil" | "both",
    target?: { x: number; y: number; z: number },
    reason: "fall" | "fail" | "restore" = "fall",
  ): void {
    const targets: Array<{ entity: Minna | Basil; spawn: Vector3; label: string }> =
      which === "both"
        ? [
            { entity: minna, spawn: config.minnaSpawn, label: "Minna" },
            { entity: basil, spawn: config.basilSpawn, label: "Basil" },
          ]
        : which === "minna"
          ? [{ entity: minna, spawn: config.minnaSpawn, label: "Minna" }]
          : [{ entity: basil, spawn: config.basilSpawn, label: "Basil" }];
    for (const { entity, spawn, label } of targets) {
      // A resolved target (checkpoint / partner / spawn from resolveRespawn)
      // only applies to single-cat respawns. `both` always uses canonical
      // spawns so the level-reset path stays deterministic.
      //
      // I2: when target is undefined for a single-cat respawn (e.g. sundew
      // 'fail'), resolve through the same checkpoint resolver the kill-plane
      // uses — so the cat lands at the checkpoint, not raw spawn.
      let dest: Vector3;
      if (which === "both") {
        dest = spawn;
      } else if (target) {
        dest = new Vector3(target.x, target.y, target.z);
      } else {
        // No explicit target: resolve via checkpoint → spawn (same as kill-plane).
        const catId = label === "Minna" ? "minna" : "basil";
        const partner = catId === "minna" ? basil : minna;
        const isCoop = !inputManager.soloMode;
        const qaActive =
          qaRulesActive();
        const checkpoint = SaveSystem.getInstance().getCheckpoint(config.levelKey);
        const catCheckpointData = checkpoint?.cats?.[catId];
        const checkpointPos =
          catCheckpointData &&
          typeof catCheckpointData.x === "number" &&
          typeof catCheckpointData.y === "number" &&
          typeof catCheckpointData.z === "number"
            ? catCheckpointData
            : null;
        const levelSpawnPos = {
          x: spawn.x,
          y: spawn.y,
          z: spawn.z,
        };
        const partnerSafe =
          partner.grounded && partner.mesh.position.y >= KILL_Y;
        const partnerPos = {
          x: partner.mesh.position.x,
          y: partner.mesh.position.y,
          z: partner.mesh.position.z,
        };
        const resolved = resolveRespawn({
          fallenCatId: catId,
          isCoop,
          qaActive,
          partnerSafe,
          partnerPos,
          checkpointPos,
          levelSpawnPos,
        });
        dest = new Vector3(resolved.pos.x, resolved.pos.y, resolved.pos.z);
      }
      const body = entity.aggregate?.body;
      // Havok DYNAMIC bodies ignore mesh.position after the first step
      // unless we toggle disablePreStep off for one pre-step. Without this
      // the respawn writes the new position and the body immediately
      // stomps it with the stale body transform.
      if (body) {
        (body as unknown as { disablePreStep: boolean }).disablePreStep = false;
      }
      entity.mesh.position.copyFrom(dest);
      entity.mesh.computeWorldMatrix(true);
      // Reset the net-drop baseline so the first post-respawn PLAYER_LANDED
      // sees netDrop = 0 instead of (preFallY − spawnY). Without this, a cat
      // that was grounded at Y=5 and respawns to Y=1 would accrue 4 wu of
      // "fall" on the settle landing, triggering spurious harness damage.
      const catId = label === "Minna" ? "minna" : "basil";
      if (catId === "basil") basilLastGroundedY = dest.y;
      else minnaLastGroundedY = dest.y;
      if (body) {
        body.setLinearVelocity(Vector3.Zero());
        body.setAngularVelocity(Vector3.Zero());
        scene.onAfterRenderObservable.addOnce(() => {
          (body as unknown as { disablePreStep: boolean }).disablePreStep = true;
        });
      }
      const now = performance.now();
      lastRespawnAt.set(label, now);
      // Always emit PLAYER_RESPAWNED (no cooldown) — QA navigators consume
      // `cat` as a fast-fail signal. The kernel companions (characterId,
      // coords, reason) are additive telemetry for HUD/feedback tasks.
      gameEvents.emit(GameEvents.PLAYER_RESPAWNED, {
        cat: catId,
        characterId: catId,
        x: dest.x,
        y: dest.y,
        z: dest.z,
        reason,
      });
      const lastToastAt = lastRespawnToastAt.get(label) ?? -Infinity;
      if (now - lastToastAt >= RESPAWN_TOAST_COOLDOWN_MS) {
        lastRespawnToastAt.set(label, now);
        gameEvents.emit(GameEvents.HUD_TOAST, {
          message: `${label} respawned.`,
        });
      }
    }
  }

  /**
   * Kill-plane check that respects a post-respawn grace window so the
   * same character can't be respawned N times in a row before its
   * physics body catches up. Called from the game loop.
   *
   * Gated on `window.__levelReady` (DEV) so the initial level-load
   * window — when cats have spawned but before each scene's
   * `createGroundAndPlatforms` has executed (the ground/plat meshes
   * often run AFTER async GLB loads, giving the cats 2-4s of
   * free-fall through the void) — does not fire respawn toasts or
   * emit respawn events. Without this gate, the first rendered
   * frames of every level showed "Minna respawned / Basil respawned"
   * toasts before the gameplay scene had fully built. Surfaced as a
   * P0 in the 2026-04-18 visual review (L3 gameplay-start void).
   */
  function killPlaneCheck(): void {
    if (import.meta.env.DEV) {
      const ready = (window as unknown as { __levelReady?: boolean }).__levelReady;
      if (!ready) return;
    }
    const now = performance.now();
    const qaActive =
      qaRulesActive();
    const isCoop = !inputManager.soloMode;
    const checkpoint = SaveSystem.getInstance().getCheckpoint(config.levelKey);

    const resolveFor = (
      fallenCatId: "minna" | "basil",
      partner: Minna | Basil,
    ): { x: number; y: number; z: number } => {
      const partnerSafe =
        partner.grounded && partner.mesh.position.y >= KILL_Y;
      const partnerPos = {
        x: partner.mesh.position.x,
        y: partner.mesh.position.y,
        z: partner.mesh.position.z,
      };
      // Guard against missing/partially-formed checkpoint (Task 1 M1 note).
      const catCheckpointData = checkpoint?.cats?.[fallenCatId];
      const checkpointPos =
        catCheckpointData &&
        typeof catCheckpointData.x === "number" &&
        typeof catCheckpointData.y === "number" &&
        typeof catCheckpointData.z === "number"
          ? catCheckpointData
          : null;
      const levelSpawnPos =
        fallenCatId === "minna"
          ? { x: config.minnaSpawn.x, y: config.minnaSpawn.y, z: config.minnaSpawn.z }
          : { x: config.basilSpawn.x, y: config.basilSpawn.y, z: config.basilSpawn.z };
      // Do NOT mutate result.pos (Task 3 LOW note — pos is returned by reference).
      const result = resolveRespawn({
        fallenCatId,
        isCoop,
        qaActive,
        partnerSafe,
        partnerPos,
        checkpointPos,
        levelSpawnPos,
      });
      return { x: result.pos.x, y: result.pos.y, z: result.pos.z };
    };

    if (minna.mesh.position.y < KILL_Y) {
      const lastMinna = lastRespawnAt.get("Minna") ?? -Infinity;
      if (now - lastMinna >= RESPAWN_GRACE_MS) {
        respawnCharacter("minna", resolveFor("minna", basil), "fall");
      }
    }
    if (basil.mesh.position.y < KILL_Y) {
      const lastBasil = lastRespawnAt.get("Basil") ?? -Infinity;
      if (now - lastBasil >= RESPAWN_GRACE_MS) {
        respawnCharacter("basil", resolveFor("basil", minna), "fall");
      }
    }
  }

  /**
   * Solo-follow state: one StuckAccumulator per level, a persisted `following`
   * boolean. All gated on `inputManager.soloMode` — never runs in co-op.
   * soloWarpIdle is declared after levelHandle (needs teleport).
   */
  const stuckAccumulator = new StuckAccumulator(0.5);
  let soloFollowing = false;

  // Level handle exposed via `scene.metadata.level` so the Babylon MCP bridge
  // (and any future dev overlay) can drive characters, inspect puzzle state,
  // and recover from soft-locks without reaching into closure-private refs.
  interface LevelHandle {
    levelKey: string;
    minnaSpawn: Vector3;
    basilSpawn: Vector3;
    resonanceSystem?: ResonanceSystem;
    respawn: (
      which: "minna" | "basil" | "both",
      target?: { x: number; y: number; z: number },
      reason?: "fall" | "fail" | "restore",
    ) => void;
    minnaEntity: Minna;
    basilEntity: Basil;
    inputManager: InputManager;
    runKillPlaneCheck: () => void;
    teleport: (which: "minna" | "basil", x: number, y: number, z?: number) => void;
    /**
     * Rehydrate the last saved checkpoint for this level: force-solve each
     * `solvedPuzzleIds` entry and reposition both cats to the stored
     * coordinates. Returns false (no-op) when no valid v2 checkpoint exists.
     * Designed to run at LOAD TIME (before `__levelReady` flips and before any
     * QA navigate starts), so the cat repositions never perturb an in-flight
     * navigator arc. Emits `PLAYER_RESPAWNED` per cat with reason:'restore'
     * (navigator contract) and `CHECKPOINT_RESTORED` once on success.
     * Fully inert under QA: when `window.__qa` is present this returns false
     * and performs NO puzzle force-solve, NO reposition, and emits nothing
     * (regardless of `__levelReady`) — per-puzzle qa:fullplay plans require
     * canonical fresh level state. Real play (no `__qa`) restores normally.
     */
    restoreFromCheckpoint: () => boolean;
    /**
     * Flip both players from their frozen KINEMATIC setup-state back
     * to DYNAMIC. Scenes invoke this after their
     * `createGroundAndPlatforms` call so gravity resumes against
     * real colliders. Idempotent — safe to call multiple times.
     */
    thawPlayers: () => void;
    getPuzzleState: () => unknown;
    getPuzzleInternals: () => unknown;
    setPuzzle: (puzzle: { getState?: () => unknown; getInternals?: () => unknown } | null) => void;
    /**
     * Invoke `attemptHit()` (or another named zero-arg method) on a named
     * ability instance. Used by the Babylon MCP bridge's
     * `trigger_ability_hit` verb to land rhythm-game hits that would
     * otherwise be effectively unhittable through `dispatch_key` due to
     * WebSocket round-trip latency.
     */
    triggerAbilityHit: (abilityName: string) => boolean;
    /**
     * Register the level's puzzle list + shared `levelState` map so the
     * dev-only `__startAtPuzzle` hook can fast-forward earlier puzzles.
     * Scenes that adopt this call invoke it once after `setup(ctx)` has
     * run for every puzzle. Legacy scenes that never call it still boot
     * normally; `__startAtPuzzle` just falls back to an event-only
     * fast-forward (emits `PUZZLE_SOLVED` per skipped puzzle, does not
     * override in-module state).
     */
    registerPuzzles: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
    /**
     * Mark a specific puzzle id as solved for the duration of this level.
     * Clamps `getState()` on that puzzle module to `{ solved: true,
     * progress: 1 }` so the game loop's per-frame `levelState.set(...)`
     * cannot stomp the forced-solve. Emits `PUZZLE_SOLVED` as a side
     * effect so downstream listeners (HUD prompts, FinalBlockage apex
     * gate, etc.) see consistent state.
     *
     * Returns `true` on success, `false` if the id is not registered.
     */
    forceSolvePuzzle: (puzzleId: string) => boolean;
    /**
     * Invoke a registered puzzle's optional `seed(state)` method. Used
     * by `__startAtPuzzle`'s `options.seedState` path to pre-advance a
     * multi-step puzzle's internal state so a trace can test just a
     * later sub-step. Returns `true` on success, `false` if the puzzle
     * isn't registered or doesn't implement `seed`.
     */
    seedPuzzle: (puzzleId: string, state: Record<string, unknown>) => boolean;
    /**
     * Aggregate QA hint bundles for every registered puzzle that has
     * opted into the hybrid QA harness via `PuzzleModule.getQAHints()`.
     * Keyed by puzzle id; puzzles that return `null` or omit the method
     * are excluded. Returns `null` when no puzzles are registered yet.
     *
     * Consumed by `tools/qa/playthrough-record.mjs` primitives
     * (`moveToTarget`, `traverse`, `activateNear`, `waitForEvent`).
     */
    getQAHints: () => Record<string, PuzzleQAHints> | null;
    /**
     * World-space AABB of every platform created via
     * `PhysicsSystem.createPlatform` — the authoritative list the
     * QA navigator uses for ceiling / walkable-surface detection.
     * Filters out decorative meshes (walkable cue planks, brass
     * lips, support beams) that a name-regex would sweep in.
     */
    getPlatforms: () => Array<{
      name: string;
      minX: number; maxX: number;
      minY: number; maxY: number;
      minZ: number; maxZ: number;
    }>;
  }

  let registeredPuzzle: { getState?: () => unknown; getInternals?: () => unknown } | null = null;
  // Registered puzzle list + shared levelState, populated by scenes that
  // opt into `__startAtPuzzle` via `registerPuzzles(...)`. Kept mutable so
  // late-registering scenes (e.g. async-loaded puzzles) still work.
  let registeredPuzzles: PuzzleModule[] = [];
  let registeredLevelState: Map<string, PuzzleState> | null = null;
  // Ids marked solved by `forceSolvePuzzle` — used by the getState wrapper
  // installed during registration so the game loop can't un-force them.
  const forcedSolvedIds = new Set<string>();

  let playersThawed = false;
  const thawPlayers = (): void => {
    if (playersThawed) return;
    playersThawed = true;
    // Runs once, after the scene shell has built every collider.
    physics.sealLevelEnds();
    try {
      minna.aggregate?.body.setMotionType(2);
      basil.aggregate?.body.setMotionType(2);
    } catch {
      // No bodies in a NullEngine test — no-op.
    }
    // Also snap velocity to zero so any imperceptible drift from the
    // kinematic phase doesn't inject initial momentum.
    try {
      minna.aggregate?.body.setLinearVelocity(Vector3.Zero());
      basil.aggregate?.body.setLinearVelocity(Vector3.Zero());
    } catch {
      // no-op
    }
  };

  levelHandle = {
    levelKey: config.levelKey,
    minnaSpawn: config.minnaSpawn,
    basilSpawn: config.basilSpawn,
    respawn: respawnCharacter,
    minnaEntity: minna,
    basilEntity: basil,
    inputManager,
    runKillPlaneCheck: killPlaneCheck,
    resonanceSystem,
    thawPlayers,
    teleport(which, x, y, z = 0) {
      const entity = which === "minna" ? minna : basil;
      const body = entity.aggregate?.body;
      // Havok v2: DYNAMIC bodies default to `disablePreStep = true` after
      // their first step, which makes the body's internal transform
      // authoritative and causes `mesh.position = …` to be discarded on
      // the next pre-step. Flip it off so the upcoming pre-step reads the
      // new mesh position, then restore it after a single render tick so
      // normal physics authority resumes.
      if (body) {
        (body as unknown as { disablePreStep: boolean }).disablePreStep = false;
      }
      entity.mesh.position.set(x, y, z);
      entity.mesh.computeWorldMatrix(true);
      // Reset net-drop baseline so post-teleport PLAYER_LANDED sees netDrop=0.
      if (which === "basil") basilLastGroundedY = y;
      else minnaLastGroundedY = y;
      if (body) {
        body.setLinearVelocity(Vector3.Zero());
        body.setAngularVelocity(Vector3.Zero());
        scene.onAfterRenderObservable.addOnce(() => {
          (body as unknown as { disablePreStep: boolean }).disablePreStep = true;
        });
      }
    },
    restoreFromCheckpoint(): boolean {
      // Checkpoint restore is fully inert under QA: per-puzzle qa:fullplay plans
      // share localStorage and require canonical fresh level state, so a restored
      // checkpoint (reposition + force-solve) would corrupt their start and break
      // the navigator's position-sensitive recipes. Real play (no __qa) restores.
      const qaActive = qaRulesActive();
      if (qaActive) return false;

      const cp = SaveSystem.getInstance().getCheckpoint(config.levelKey);
      // Guard: missing / partially-formed checkpoint → no-op (Task 1 M1).
      if (!cp) return false;
      if (
        !cp.solvedPuzzleIds ||
        !cp.cats ||
        !cp.cats.minna ||
        !cp.cats.basil ||
        typeof cp.cats.minna.x !== "number" ||
        typeof cp.cats.minna.y !== "number" ||
        typeof cp.cats.minna.z !== "number" ||
        typeof cp.cats.basil.x !== "number" ||
        typeof cp.cats.basil.y !== "number" ||
        typeof cp.cats.basil.z !== "number"
      ) {
        return false;
      }

      // C1: suppress checkpoint capture while replaying solved puzzles so the
      // synthetic PUZZLE_SOLVED events emitted by forceSolvePuzzle cannot
      // overwrite the persisted checkpoint with spawn-position cat coords.
      restoringRef.value = true;
      try {
        // Re-apply each previously solved puzzle. forceSolvePuzzle is a
        // no-op for ids not registered yet, so ordering with registerPuzzles
        // doesn't matter for load-time restore.
        for (const id of cp.solvedPuzzleIds) {
          this.forceSolvePuzzle(id);
        }
      } finally {
        restoringRef.value = false;
      }

      // Reposition both cats to the stored coordinates using the same
      // disablePreStep dance as teleport(). Emit PLAYER_RESPAWNED per cat
      // with reason:'restore' + characterId + coords (R1) so Task 7's
      // telemetry holds.
      const place = (
        entity: typeof minna | typeof basil,
        pos: { x: number; y: number; z: number },
        cat: "minna" | "basil",
      ): void => {
        const body = entity.aggregate?.body;
        if (body) {
          (body as unknown as { disablePreStep: boolean }).disablePreStep = false;
        }
        entity.mesh.position.set(pos.x, pos.y, pos.z);
        entity.mesh.computeWorldMatrix(true);
        // Reset net-drop baseline so post-restore PLAYER_LANDED sees netDrop=0.
        if (cat === "basil") basilLastGroundedY = pos.y;
        else minnaLastGroundedY = pos.y;
        if (body) {
          body.setLinearVelocity(Vector3.Zero());
          body.setAngularVelocity(Vector3.Zero());
          scene.onAfterRenderObservable.addOnce(() => {
            (body as unknown as { disablePreStep: boolean }).disablePreStep = true;
          });
        }
        gameEvents.emit(GameEvents.PLAYER_RESPAWNED, {
          cat,
          characterId: cat,
          x: pos.x,
          y: pos.y,
          z: pos.z,
          reason: "restore",
        });
      };
      place(minna, cp.cats.minna, "minna");
      place(basil, cp.cats.basil, "basil");

      gameEvents.emit(GameEvents.CHECKPOINT_RESTORED, {
        levelId: cp.levelId,
        puzzleIndex: cp.puzzleIndex,
      });
      return true;
    },
    getPuzzleState() {
      return registeredPuzzle?.getState?.() ?? null;
    },
    getPuzzleInternals() {
      return registeredPuzzle?.getInternals?.() ?? null;
    },
    setPuzzle(puzzle) {
      registeredPuzzle = puzzle;
    },
    triggerAbilityHit(abilityName) {
      // Look across both holders so this verb doesn't care whether the
      // ability belongs to Minna or Basil — the name is unique per game.
      const ability =
        basilAbilities.getAbility(abilityName) ??
        minnaAbilities.getAbility(abilityName);
      if (!ability) return false;
      const fn = (ability as unknown as { attemptHit?: () => boolean }).attemptHit;
      if (typeof fn !== "function") return false;
      return fn.call(ability);
    },
    registerPuzzles(puzzles, levelState) {
      registeredPuzzles = puzzles;
      registeredLevelState = levelState;
      // Wrap each puzzle's getState() so `forceSolvePuzzle` can clamp
      // it to solved without the puzzle module having to expose a
      // `markSolved()` method. We patch once per puzzle; re-registration
      // (e.g. hot-reload) replaces the list.
      for (const puzzle of puzzles) {
        const origGetState = puzzle.getState.bind(puzzle);
        puzzle.getState = () => {
          if (forcedSolvedIds.has(puzzle.id)) {
            return { solved: true, progress: 1 };
          }
          return origGetState();
        };
      }
    },
    forceSolvePuzzle(puzzleId) {
      const puzzle = registeredPuzzles.find((p) => p.id === puzzleId);
      if (!puzzle) return false;
      forcedSolvedIds.add(puzzleId);
      if (registeredLevelState) {
        registeredLevelState.set(puzzleId, { solved: true, progress: 1 });
      }
      gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId });
      // Let the puzzle replay its real solve-path side effects (e.g.
      // MasterValve flips the ConservatoryCycle to Irrigation so
      // NectarEscort actually runs). Puzzles without side effects
      // can omit the hook.
      try {
        puzzle.onForceSolve?.();
      } catch (e) {
        console.warn(
          `[BaseLevel] onForceSolve threw for ${puzzleId}: ${(e as Error).message}`,
        );
      }
      return true;
    },
    seedPuzzle(puzzleId, state) {
      const puzzle = registeredPuzzles.find((p) => p.id === puzzleId);
      if (!puzzle || typeof puzzle.seed !== "function") return false;
      try {
        puzzle.seed(state);
        return true;
      } catch (e) {
        console.warn(
          `[BaseLevel] seed(${puzzleId}) threw: ${(e as Error).message}`,
        );
        return false;
      }
    },
    getQAHints() {
      if (registeredPuzzles.length === 0) return null;
      const out: Record<string, PuzzleQAHints> = {};
      for (const puzzle of registeredPuzzles) {
        const hints = puzzle.getQAHints?.();
        if (hints) out[puzzle.id] = hints;
      }
      return Object.keys(out).length > 0 ? out : null;
    },
    getPlatforms() {
      return physics.getPlatformRegistry();
    },
  };
  scene.metadata = {
    ...(scene.metadata ?? {}),
    level: levelHandle,
    // Expose the CameraSystem so the dev-only `__startAtPuzzle` hook
    // can snap the camera after teleporting cats to a new puzzle's
    // origin. Keeps the hook self-contained (no dependency on the
    // scene shell passing a camera ref into the hook definition).
    cameraSystem: camera,
    // Expose the InputManager for QA / dev introspection. Lets the
    // Babylon MCP bridge probe input state during diagnostic
    // sessions (e.g. verifying a key dispatch reaches the active
    // input source). Not a public game API.
    inputManager,
    // Cat entities — consumed by `window.__qa.scene.cat(id)` for
    // velocity + grounded-state introspection. Mesh-only lookup
    // (`scene.getMeshByName`) works for position but can't reach the
    // Havok body or grounded flag without an entity reference.
    minna,
    basil,
  };

  /**
   * Warp the idle cat adjacent to the active cat (solo-only).
   * Placed after levelHandle so it can call levelHandle.teleport.
   * Only ever invoked from the game loop after both levelHandle and all
   * cats are fully initialised.
   */
  function soloWarpIdle(
    idle: typeof minna | typeof basil,
    active: typeof minna | typeof basil,
  ): void {
    if (!inputManager.soloMode) return;
    const activePos = active.mesh.position;
    // Place the idle cat on the active cat's interior side (toward where
    // the active cat came from) so we never overshoot a platform edge.
    // Use the active cat's exact X with a small +Y clearance as fallback
    // when the idle cat is already on the same side (dx ≈ 0).
    const dx = activePos.x - idle.mesh.position.x;
    const offsetX = dx > 0.1 ? -1.5 : dx < -0.1 ? 1.5 : 0;
    levelHandle.teleport(
      idle === minna ? "minna" : "basil",
      activePos.x + offsetX,
      activePos.y + 0.1,  // small clearance so the warp lands above the surface
      activePos.z,
    );
    stuckAccumulator.reset();
  }

  // Reset solo-follow state on Tab switch so the newly-active cat's
  // companionWalk = 0 takes effect immediately and the accumulator
  // doesn't carry over stale stranded time from the previous active cat.
  unsubs.push(
    gameEvents.on(GameEvents.PLAYER_SWITCHED, () => {
      stuckAccumulator.reset();
      soloFollowing = false;
    }),
  );

  // Attach the engine-side navigator. It needs the scene's platform
  // registry + level handle + cat entities, all of which are now set on
  // scene.metadata above. Dev-only — production builds skip the QASurface
  // that drives it so the tick observer is wasted work.
  if (import.meta.env.DEV) {
    getNavigationSystem().attach(scene);
  }

  const reportRuntimeError = (source: string, error: unknown): void => {
    if (runtimeFailed) {
      return;
    }
    runtimeFailed = true;
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("[Cats of Chronos] Runtime error in %s (%s):", config.levelKey, source, error);
    gameEvents.emit(GameEvents.LEVEL_CRASHED, {
      levelKey: config.levelKey,
      errorMessage,
    });
  };

  function dispose(): void {
    unsubs.forEach(fn => fn());
    disposeCallbacks.forEach(fn => fn());
    audioManager.dispose();
    cutsceneSystem.dispose();
    abilityVfx.dispose();
    highlightLayer.dispose();
    glowLayer.dispose();
    abilityMapper.dispose();
    inputManager.dispose();
    if (import.meta.env.DEV) {
      getNavigationSystem().detach();
    }
  }

  // Register dispose on scene dispose
  scene.onDisposeObservable.addOnce(() => {
    dispose();
  });

  const ctx: BaseLevelContext = {
    scene,
    physics,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    overclockedLeap,
    camera,
    inputManager,
    audioManager,
    cutsceneSystem,
    get glowLayer() { return glowLayer; },
    getGlowLayer() { return glowLayer; },
    setGlowLayer(nextGlowLayer: GlowLayer) {
      if (glowLayer === nextGlowLayer) {
        return;
      }
      glowLayer.dispose();
      glowLayer = nextGlowLayer;
      auraSight.setEffectLayers(glowLayer, highlightLayer);
      // Re-apply the co-regulation stream exclusion on the new layer.
      if (resonanceStreamMesh) {
        glowLayer.addExcludedMesh(resonanceStreamMesh);
      }
    },
    getHighlightLayer() { return highlightLayer; },
    basilEnergy,
    minnaEnergy,
    harnessWounds,
    abilityMapper,
    // This is set as a getter so the level can assign it after lighting setup
    get shadowGenerator() { return shadowGenerator; },
    set shadowGenerator(sg: ShadowGenerator) { shadowGenerator = sg; },

    registerGameLoop(
      puzzleUpdate: (dt: number, sceneTime: number) => void,
      options?: {
        isLevelComplete?: () => boolean;
        onAbilityKey?: (key: string, pressed: boolean, released: boolean) => boolean;
      },
    ): void {
      let sceneTime = 0;
      // Tracks Minna's depletion across frames so we can restore AuraSight's
      // full blur exactly once when depletion ENDS while the aura is still
      // held active (otherwise blur stays stuck at its last modulated value).
      let wasMinnaDepleted = false;
      const isComplete = options?.isLevelComplete ?? (() => false);
      const onAbilityKey = options?.onAbilityKey;

      // Fixed-step game loop. With the engine's deterministic lockstep
      // (core/Engine.ts) gameplay runs once per physics step at exactly
      // SIM_STEP_SECONDS, before Havok steps, so behaviour no longer depends
      // on the display's frame rate (playtest: stopping distance varied 4×
      // between 30 and 144 Hz, and the old apex re-jump only reproduced at
      // 60 Hz). Engines without lockstep (unit-test NullEngines) fall back
      // to the previous per-render loop.
      const engine = scene.getEngine();
      const lockstep = engine.isDeterministicLockStep();
      const stepObservable = lockstep ? scene.onBeforeStepObservable : scene.onBeforeRenderObservable;
      stepObservable.add(() => {
        if (runtimeFailed || isComplete() || cutsceneSystem.playing) return;

        try {
          const dt = lockstep
            ? engine.getTimeStep() / 1000
            : clampDeltaTime(engine.getDeltaTime() / 1000);
          sceneTime += dt;

          inputManager.update();

          // --- Resonance Stream Update ---
          resonanceSystem.update(dt);

          // --- Kill plane: recover cats that fell into the void ---
          killPlaneCheck();

          // --- Solo catch-up follow + stranded-warp ---
          // In solo mode the idle cat trails the active cat autonomously via
          // decideSoloFollow (hysteresis: starts following at triggerDist 6u,
          // stops at stopDeadband 1.5u). If the idle cat gets stuck for
          // warpDwellSeconds (2.5 s) while more than warpDist (15u) away it
          // is warped adjacent to the active cat. Warp is hard-gated on
          // soloMode && !qaActive so it can never fire during co-op or QA
          // real-play runs (which use navigatePair with soloMode=false).
          // In co-op both companionWalks are zeroed as before.
          // QA navigator sets soloMode=true for single-cat navigate, so ALL
          // solo-assist (follow, warp, relaxed tether) is gated off when the
          // QA surface is present — QA must see canonical mechanics.
          const qaActive =
            qaRulesActive();
          if (inputManager.soloMode) {
            const active = inputManager.activePlayer === 1 ? minna : basil;
            const idle   = inputManager.activePlayer === 1 ? basil : minna;

            // Tab-switch race: active cat must never be follow-driven.
            active.companionWalk = 0;

            const idleX = idle.mesh.position.x;
            const secondsStranded = stuckAccumulator.update(idleX, dt);

            const result = decideSoloFollow({
              soloMode: true,
              qaActive,
              activeX: active.mesh.position.x,
              idleX,
              secondsStranded,
              triggerDist: 6,
              stopDeadband: 1.5,
              warpDist: 15,
              warpDwellSeconds: 2.5,
              following: soloFollowing,
              // Hold station: a cat parked on a pressed plate or inside an
              // open portal stays put — chasing the active cat off a post
              // the player deliberately chose breaks every park-one-cat
              // mechanic (Orrery sync plates, dual-entry portals).
              idleOnStation: isOnHoldStation(scene, idle.mesh.position),
            });

            soloFollowing = result.following;
            idle.companionWalk = result.companionWalk;

            if (result.warp) {
              soloWarpIdle(idle, active);
              soloFollowing = false;
            }
          } else {
            minna.companionWalk = 0;
            basil.companionWalk = 0;
          }

          // --- Player movement ---
          if (inputManager.soloMode) {
            const activeInput = inputManager.getActiveInput();
            const idleInput = inputManager.getIdleInput();

            if (inputManager.activePlayer === 1) {
              minna.applyInput(activeInput, dt);
              basil.applyInput(idleInput, dt);
            } else {
              minna.applyInput(idleInput, dt);
              basil.applyInput(activeInput, dt);
            }
          } else {
            minna.applyInput(inputManager.getInput(1), dt);
            basil.applyInput(inputManager.getInput(2), dt);
          }

          // --- Shared Camera edge restriction (canon-load-bearing) ---
          // Per 2026-04-14 companion book Section III, the frame itself
          // is the instructor — straying too far damps outward movement.
          // Runs AFTER applyInput so the damping clamps the velocity the
          // player just tried to set, and BEFORE physics stepping so the
          // Havok tick sees the clamped velocity.
          // In solo mode the idle cat follows autonomously, so the
          // canonical co-op tether would frustrate the player by damping
          // movement before the partner has had a chance to catch up.
          // Pass a relaxed threshold (2× MAX_SEPARATION) for solo; co-op
          // uses the strict default so the behaviour is byte-identical.
          // soloMaxSep: pass 2× MAX_SEPARATION in solo so the tether is relaxed
          // while the idle cat catches up. Cast via addition to widen from the
          // literal type `12` that TypeScript infers for the parameter default.
          // Relaxed tether applies in ALL solo (including QA single-cat
          // navigate). It is strictly LESS restrictive than the co-op default,
          // so it never fights the navigator — unlike the follow/warp, which
          // are qaActive-gated. Co-op (soloMode=false) keeps the strict 12u
          // default byte-identically.
          const soloMaxSepNum = inputManager.soloMode
            ? PLAYER.MAX_SEPARATION * 2
            : PLAYER.MAX_SEPARATION;
          minna.clampToSeparation(basil.mesh.position.x, soloMaxSepNum as typeof PLAYER.MAX_SEPARATION);
          basil.clampToSeparation(minna.mesh.position.x, soloMaxSepNum as typeof PLAYER.MAX_SEPARATION);

          // --- Ability keybinds (centralized via AbilityInputMapper) ---
          abilityMapper.processInput(
            inputManager.getAbilityInput(),
            minnaAbilities,
            basilAbilities,
            onAbilityKey,
          );

          // --- Ability updates ---
          minnaAbilities.update(dt);
          basilAbilities.update(dt);

          // --- Harness Wound System (I1: net-drop tracking) ---
          // While each cat IS grounded, update lastGroundedY so we always
          // know the Y they last stood on. When airborne the value freezes,
          // giving us the takeoff height. On PLAYER_LANDED (above) we compute
          // netDrop = max(0, lastGroundedY - landingY) — zero for a jump that
          // returns to the same height, positive only for genuine ledge falls.
          // NAVIGATOR SAFETY: reads the public `grounded` getter only.
          if (basil.grounded) {
            basilLastGroundedY = basil.mesh.position.y;
          }
          if (minna.grounded) {
            minnaLastGroundedY = minna.mesh.position.y;
          }

          // Compose the harness move-speed floor with the energy
          // depletion floor — whichever is slower wins. Done BEFORE the
          // energy block so the energy-depleted path still clobbers
          // `basil.moveSpeedScale` on its own branch (the final value is
          // the minimum of the two).
          const harnessMoveScale = harnessWounds.tuning.moveSpeedScale;

          // Passive-idle harness regen (always ticks — idle regen only
          // kicks in after the spec's PASSIVE_REGEN_DELAY).
          harnessWounds.update(dt);

          // Purr heal: while Minna's CalmingResonance is active AND
          // Basil is inside the co-regulation distance, heal the
          // harness by PURR_PER_SEC. Mirrors the spec's "Primary: Purr
          // Repair" loop.
          if (calmingResonance.isActive) {
            const purrDist = Vector3.Distance(minna.mesh.position, basil.mesh.position);
            if (purrDist <= ENERGY.COREGULATION.DISTANCE) {
              harnessWounds.heal(HARNESS.HEAL.PURR_PER_SEC * dt, "purr");
            }
          }

          // --- Energy system updates + Co-Regulation ---
          if (basilEnergy && minnaEnergy) {
            // Depleted speed penalty for Basil. Take the slower of the
            // two scales so a depleted AND wounded Basil reads as
            // cumulatively slower (by design, per spec §3).
            const energyScale = basilEnergy.isDepleted
              ? ENERGY.BASIL.DEPLETED_SPEED_SCALE
              : 1.0;
            basil.moveSpeedScale = Math.min(energyScale, harnessMoveScale);
            // Basil: drain PneumaticAnchor while held. Harness state
            // scales the drain per spec §3 (Venting drains faster).
            if (pneumaticAnchor.isActive) {
              const drainScale = pneumaticAnchor.drainScale;
              const ok = basilEnergy.drain(
                ENERGY.BASIL.DRAIN.PNEUMATIC_ANCHOR_PER_SEC * drainScale * dt,
              );
              if (!ok || basilEnergy.isDepleted) {
                basilAbilities.deactivateAbility('pneumaticAnchor');
              }
            }

            // Minna: drain while AuraSight / ShadowSlip active
            if (auraSight.isActive) {
              minnaEnergy.drain(ENERGY.MINNA.DRAIN.AURA_SIGHT * dt);
            }
            if (shadowSlip.isActive) {
              minnaEnergy.drain(ENERGY.MINNA.DRAIN.SHADOW_SLIP * dt);
            }

            // Minna: recharge in shadow zones (shadowSlip tracks zone overlap)
            if (shadowSlip.isInShadowZone) {
              minnaEnergy.recharge(ENERGY.MINNA.PASSIVE_RECHARGE * dt);
            }

            // Co-Regulation: proximity check
            const dist = Vector3.Distance(minna.mesh.position, basil.mesh.position);
            if (dist <= ENERGY.COREGULATION.DISTANCE) {
              basilEnergy.recharge(ENERGY.COREGULATION.BONUS_PER_SEC * dt);
              minnaEnergy.recharge(ENERGY.COREGULATION.BONUS_PER_SEC * dt);
              if (!coRegulationActive) {
                coRegulationActive = true;
                gameEvents.emit(GameEvents.COREGULATION_ACTIVE);
              }
            } else if (coRegulationActive) {
              coRegulationActive = false;
              gameEvents.emit(GameEvents.COREGULATION_ENDED);
            }

            // Depleted penalties: CalmingResonance range reduction
            calmingResonance.radiusScale = minnaEnergy.isDepleted
              ? ENERGY.MINNA.DEPLETED_CALMING_RANGE_SCALE
              : 1.0;

            // Depleted penalties: AuraSight — continuous blur/particle modulation
            // instead of a ~15Hz HighlightLayer enable/disable toggle.
            // reducedMotion: hold a steady mid-dim value (no oscillation).
            if (minnaEnergy.isDepleted && auraSight.isActive) {
              const reducedMotion = AccessibilitySystem.getInstance().state.reducedMotion;
              const depletionT = reducedMotion
                ? 0.5
                : 0.5 + 0.5 * Math.sin(sceneTime * 15);
              auraSight.setDepletionIntensity(depletionT);
            } else if (wasMinnaDepleted && !minnaEnergy.isDepleted && auraSight.isActive) {
              // Depletion just ended while the aura is still held — restore the
              // full (non-depleted) blur once instead of leaving it stuck low.
              auraSight.setDepletionIntensity(1);
            }
            wasMinnaDepleted = minnaEnergy.isDepleted;

            // Depleted penalties: Minna ShadowSlip random fail
            if (minnaEnergy.isDepleted && shadowSlip.isActive) {
              // 30% chance per second -> probability per frame = 1 - (1 - 0.3)^dt
              const failProb = 1 - Math.pow(1 - ENERGY.MINNA.DEPLETED_SHADOW_FAIL_CHANCE, dt);
              if (Math.random() < failProb) {
                minnaAbilities.deactivateAbility('shadowSlip');
              }
            }

            // Update energy systems (passive recharge)
            basilEnergy.update(dt);
            minnaEnergy.update(dt);
          } else {
            // Energy system disabled — harness scale still applies so a
            // wounded Basil slows down correctly on tutorial levels.
            basil.moveSpeedScale = harnessMoveScale;
          }

          // --- Level-specific puzzle update ---
          puzzleUpdate(dt, sceneTime);

          // --- Camera --- (per render frame under lockstep; see below)
          if (!lockstep) camera.update(dt);

          // --- Store prev key state for next frame edge detection ---
          abilityMapper.endFrame();
        } catch (error) {
          reportRuntimeError("gameLoop", error);
        }
      });

      if (lockstep) {
        // Record each cat's physics position after every step, then on each
        // rendered frame draw the models between the last two steps so a
        // 120/144 Hz display doesn't show 60 Hz stepping.
        scene.onAfterStepObservable.add(() => {
          minna.recordStepPosition();
          basil.recordStepPosition();
        });
        scene.onBeforeRenderObservable.add(() => {
          if (runtimeFailed || isComplete() || cutsceneSystem.playing) return;
          try {
            const accumulator = (scene as unknown as { _timeAccumulator?: number })._timeAccumulator ?? 0;
            const alpha = Math.min(1, Math.max(0, accumulator / engine.getTimeStep()));
            minna.interpolateVisual(alpha);
            basil.interpolateVisual(alpha);
            camera.update(clampDeltaTime(engine.getDeltaTime() / 1000));
          } catch (error) {
            reportRuntimeError("renderLoop", error);
          }
        });
      }
    },

    addUnsub(unsub: () => void): void {
      unsubs.push(unsub);
    },

    addDisposeCallback(fn: () => void): void {
      disposeCallbacks.push(fn);
    },

    registerSafeBeforeRender(name: string, callback: () => void): () => void {
      const wrapped = () => {
        if (runtimeFailed) {
          return;
        }
        try {
          callback();
        } catch (error) {
          reportRuntimeError(name, error);
        }
      };
      scene.registerBeforeRender(wrapped);
      return () => scene.unregisterBeforeRender(wrapped);
    },

    dispose,
  } as BaseLevelContext;

  return ctx;
}
